import { useEffect, useState, type ReactNode } from 'react'
import { Search } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'

import { cn } from '../lib/cn'
import { t } from '../lib/i18n'

/* ── SearchInput ──────────────────────────────────────────────────────────
   Debounced and synced to the URL, so a support call can be handed over by
   pasting a link rather than describing which filter was on.
   ────────────────────────────────────────────────────────────────────────── */

export function SearchInput({
  placeholder,
  paramName = 'q',
  debounceMs = 250,
  className,
}: {
  placeholder?: string
  paramName?: string
  debounceMs?: number
  className?: string
}) {
  const [params, setParams] = useSearchParams()
  const committed = params.get(paramName) ?? ''
  const [draft, setDraft] = useState(committed)

  // The URL is the source of truth; a back button must move the box.
  useEffect(() => setDraft(committed), [committed])

  useEffect(() => {
    if (draft === committed) return
    const id = window.setTimeout(() => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          if (draft) next.set(paramName, draft)
          else next.delete(paramName)
          return next
        },
        { replace: true },
      )
    }, debounceMs)
    return () => window.clearTimeout(id)
  }, [draft, committed, debounceMs, paramName, setParams])

  return (
    <label
      className={cn(
        'flex h-[38px] min-w-[250px] items-center gap-2.5 rounded-[10px] px-3.5',
        'gl',
        className,
      )}
    >
      <Search className="z size-[15px] shrink-0 text-muted" />
      <input
        type="search"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder={placeholder ?? t('search.placeholder')}
        aria-label={placeholder ?? t('search.placeholder')}
        className="z w-full bg-transparent text-[13px] text-dark outline-none placeholder:text-light"
      />
    </label>
  )
}

/** Read the committed search term. Screens filter with this, never with state. */
export function useSearchTerm(paramName = 'q'): string {
  const [params] = useSearchParams()
  return (params.get(paramName) ?? '').trim().toLowerCase()
}

/* ── FilterPills ──────────────────────────────────────────────────────────── */

export interface FilterPill<Value extends string> {
  value: Value
  label: ReactNode
}

export function FilterPills<Value extends string>({
  pills,
  value,
  onChange,
  className,
  label,
}: {
  pills: FilterPill<Value>[]
  value: Value
  onChange: (value: Value) => void
  className?: string
  label?: string
}) {
  return (
    <div
      role="group"
      aria-label={label ?? t('filter.label')}
      className={cn('flex flex-wrap items-center gap-2', className)}
    >
      {pills.map((pill) => {
        const selected = pill.value === value
        return (
          <button
            key={pill.value}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(pill.value)}
            className={cn(
              'inline-flex h-[34px] items-center gap-1.5 rounded-[9px] px-3.5',
              'border text-[12.5px] font-semibold whitespace-nowrap',
              selected
                ? 'border-accent-glass-border bg-accent-glass text-accent-dark'
                : 'border-glass-border bg-glass text-sub',
            )}
          >
            {pill.label}
          </button>
        )
      })}
    </div>
  )
}

/** URL-synced variant, for the filters that belong in a shareable link. */
export function useUrlFilter<Value extends string>(
  paramName: string,
  fallback: Value,
  allowed: readonly Value[],
): [Value, (next: Value) => void] {
  const [params, setParams] = useSearchParams()
  const raw = params.get(paramName)
  const value = allowed.includes(raw as Value) ? (raw as Value) : fallback

  const set = (next: Value) => {
    setParams(
      (current) => {
        const updated = new URLSearchParams(current)
        if (next === fallback) updated.delete(paramName)
        else updated.set(paramName, next)
        return updated
      },
      { replace: true },
    )
  }

  return [value, set]
}

/* ── FilterBar — the toolbar row above a table ────────────────────────────── */

export function FilterBar({
  children,
  className,
  trailing,
}: {
  children: ReactNode
  className?: string
  trailing?: ReactNode
}) {
  return (
    <div className={cn('mb-3.5 flex flex-wrap items-center gap-2.5', className)}>
      {children}
      {trailing ? <div className="ms-auto flex items-center gap-2">{trailing}</div> : null}
    </div>
  )
}

/* ── Pagination — cursor, not page numbers ────────────────────────────────
   The API pages by opaque cursor (`packages/contracts`), so there is no page
   count to render and no "jump to page 7". Two directions and a count is the
   whole vocabulary.
   ────────────────────────────────────────────────────────────────────────── */

export function Pagination({
  shown,
  total,
  onOlder,
  onNewer,
  hasOlder = false,
  hasNewer = false,
  className,
}: {
  shown: number
  total: number
  onOlder?: () => void
  onNewer?: () => void
  hasOlder?: boolean
  hasNewer?: boolean
  className?: string
}) {
  return (
    <div
      className={cn(
        'mt-3.5 flex items-center justify-between gap-3 border-t border-muted/15 pt-3.5',
        className,
      )}
    >
      <p className="text-xs text-muted">{t('table.showingOf', { shown, total })}</p>
      {onNewer || onOlder ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!hasNewer}
            onClick={onNewer}
            className="h-8 rounded-lg border border-glass-border bg-glass px-3 text-xs font-semibold text-sub disabled:opacity-40"
          >
            {t('table.newer')}
          </button>
          <button
            type="button"
            disabled={!hasOlder}
            onClick={onOlder}
            className="h-8 rounded-lg border border-glass-border bg-glass px-3 text-xs font-semibold text-sub disabled:opacity-40"
          >
            {t('table.older')}
          </button>
        </div>
      ) : null}
    </div>
  )
}
